import { Flex, Button, Heading, HStack, Text, Box, CloseButton } from "@chakra-ui/react";
import { AirhelpPlus } from "@airhelp/react";
import { getDynamicVariable } from "config/dynamicVariables";
import { MAX_GRID_WIDTH } from "config/styleConsts";
import { useLanguage, useTranslations } from "contexts/LocalizationContext";
import { useStickyBottomBarContext } from "contexts/StickyBottomBarContext";
import { ArrowRightIcon } from "styles/icons";
import type { StickyBottomBarIconProps, StickyBottomBarProps } from "../StickyBottomBar.d";
import { getCTAElementProps } from "../utils/getCTAElementProps";
import { stickyBottomBarDesktopStyles } from "./StickyBottomBarDesktop.styles";

const StickyBottomBarDesktop = ({
  iconsWithText,
  heading,
  variant,
}: {
  iconsWithText: StickyBottomBarIconProps[];
  heading: StickyBottomBarProps["heading"];
  variant: StickyBottomBarProps["variant"];
}) => {
  const language = useLanguage();
  const translations = useTranslations();
  const { closeBottomBar } = useStickyBottomBarContext();
  const CTAButtonProps = getCTAElementProps(language, variant);

  const customHeading = {
    basic:
      heading ||
      translations("claim_up_to_compensation", {
        "%compensation%": getDynamicVariable("{{compensation_value}}", language),
      }),
    ahPlus: heading || translations("be_prepared_for_future_flight_disruptions"),
  }[variant];

  const buttonText = {
    basic: translations("check_compensation"),
    ahPlus: translations("from_commission_value_per-year", {
      "%commission_value%": getDynamicVariable("{{ah_price_value}}"),
    }),
  }[variant] as string;

  return (
    <Box
      p={{ lg: 6 }}
      color="greyscale.100"
      borderRadius="lg"
      mb={8}
      maxW={MAX_GRID_WIDTH}
      mx={{ lg: 4, xl: "auto" }}
      sx={stickyBottomBarDesktopStyles[variant].container}
      maxH={40}
    >
      <Flex position="relative" sx={stickyBottomBarDesktopStyles[variant].innerContainer}>
        <CloseButton
          position="absolute"
          sx={stickyBottomBarDesktopStyles[variant].closeButton}
          onClick={closeBottomBar}
          aria-label="Close bottom bar"
        />
        <AirhelpPlus sx={stickyBottomBarDesktopStyles[variant].logo} />
        <Box sx={stickyBottomBarDesktopStyles[variant].headingContainer}>
          <Heading as="h4" variant="h4" sx={stickyBottomBarDesktopStyles[variant].heading}>
            {customHeading}
          </Heading>
        </Box>

        <Flex justify="space-between" sx={stickyBottomBarDesktopStyles[variant].innerSubContainer}>
          <HStack spacing={10} sx={stickyBottomBarDesktopStyles[variant].iconsWithText}>
            {iconsWithText.map(({ textKey, icon: Icon }) => (
              <Flex align="center" key={textKey}>
                <Flex borderRadius="50%" border="2px solid" borderColor="primary.400" mr={2} as="span" p={2}>
                  <Icon boxSize={5} />
                </Flex>
                <Text color="white" as="span" fontSize="sm">
                  {translations(textKey)}
                </Text>
              </Flex>
            ))}
          </HStack>
          <Button
            as="a"
            variant="subtle"
            sx={{ svg: { boxSize: 4 }, ...stickyBottomBarDesktopStyles[variant].actionButton }}
            {...CTAButtonProps}
          >
            {buttonText} <ArrowRightIcon ml={3} />
          </Button>
        </Flex>
      </Flex>
    </Box>
  );
};

export default StickyBottomBarDesktop;
